import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, CardContent, CircularProgress, FormControl, Grid, Paper, TextField, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useVForm } from 'shared/forms';
import { FerramentasDeDetalhe, InputError, ConfirmacaoModal} from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { ChaveValorService } from 'shared/services/chaveValor/chaveValorService';
import { ICreateChaveValor } from 'shared/types';
import { t } from 'i18next';
import { useHasPermissionAcao } from 'shared/hooks';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import env from 'config/env';
import { enUS, ptBR } from 'date-fns/locale';
import { dateToLocalISO, stringToLocalISO } from 'shared/utils';


const validationSchema = yup.object({
  CodigoChaveValor: yup.number().moreThan(0),
  Descricao: yup.string().required(),
  Valor: yup.string().required(), 
  dhAlteracao: yup.string().required(),
});

export const DetalheDeChaveValor: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();
  
  const hasPermitionUpdate = useHasPermissionAcao('configChaveValor','UPD');

  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, save, isSaveAndClose } = useVForm();

  const { register, handleSubmit, watch, reset, setValue, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const dhAlteracao = watch('dhAlteracao');

  const localeSel = (env.language === 'en' ? enUS : ptBR);
  const localeMask = (env.language === 'en' ? '__/__/____ __:__:__ _M' : '__/__/____ __:__:__');
  const localeInputFormat = (env.language === 'en' ? 'MM/dd/yyyy hh:mm:ss a' : 'dd/MM/yyyy HH:mm:ss');
  const lenDate = (env.language === 'en' ? 22 : 19);

  const handleChangeDhAlteracao = (valor: string) => setValue('dhAlteracao', valor);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [messageConfirmacao, setMessageConfirmacao] = useState(''); 

  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      ChaveValorService.getById(id)
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate('/configChaveValor/listagem');
          } else {
            reset(result.rows[0]);
          }
          setIsLoading(false);
        });
    }
    
  }, [id]);

 

  const handleDelete = () => {
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.organizationSeletion.exclusion') + id + '?');
    setOpenConfirmacao(true);
    console.log('handleDelete');
  };

  const confirmDelete = () => {
    setAlert(null);
    ChaveValorService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'error',
          });
          navigate('/organizacao/listagem');
        }
      })
      .catch(reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateChaveValor) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      ChaveValorService
        .create(data)
        .then((result) => { 
          setIsLoading(false);

          if (result instanceof Error) {  
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/configChaveValor/listagem');
            } else {
              navigate(`/configChaveValor/detalhe/${result}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    } else {
      ChaveValorService
        .updateById(String(id), data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            if (isSaveAndClose()) {
              navigate('/configChaveValor/listagem');
            }
            setAlert({
              text: t('messages.success.alterado'),
              type: 'success',
            });

          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }

  };



  return (
    <LayoutBaseDePagina
     
      titulo={id === 'novo' ? t('pages.chaveValor.NewChaveValor') : hasPermitionUpdate?t('pages.chaveValor.changeChaveValor'):t('pages.organizationSeletion.viewOrg')}
      barraDeFerramentas={!(isLoading || isSubmitting) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDechaveValor'          

          mostrarBotaoNovo={false}
          mostrarBotaoApagar={false}
          mostrarBotaoSalvar={id == 'novo' || hasPermitionUpdate}
          aoClicarEmSalvar={save}
          aoClicarEmVoltar={() => navigate('/configChaveValor/listagem')}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/configChaveValor/detalhe/novo')}
        />)
      }
    >
      <Box ref={formRef} id='formDetalheDechaveValor' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.CodigoChaveValor?.type ? true : false}
                      fullWidth
                      id='Codigo'
                      type='text'
                      label={`${t('pages.chaveValor.codigo')} *`}
                      {...register('Codigo')}
                      disabled={isSubmitting || isLoading || id !== 'novo'}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.Codigo?.type && <InputError type={errors.Codigo.type} field='Codigo' />}
                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.Descricao?.type ? true : false}
                      fullWidth
                      id='Descricao'
                      type='text'
                      label={`${t('pages.chaveValor.desc')} *`}
                      {...register('Descricao')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                    />
                    {errors?.Descricao?.type && <InputError type={errors.Descricao.type} field='Descricao' />}
                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.Valor?.type ? true : false}
                      fullWidth
                      id='Valor'
                      type='text'
                      label={`${t('pages.chaveValor.valor')} *`}
                      {...register('Valor')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                      inputProps={{maxLength:100}}
                    />
                    {errors?.Valor?.type && <InputError type={errors.Valor.type} field='Valor' />}
                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <TextField
                      error={errors?.usuAlteracao?.type ? true : false}
                      fullWidth
                      disabled={true}
                      id='usuAlteracao'
                      type='text'
                      label={`${t('pages.chaveValor.alt')} *`}
                      {...register('usuAlteracao')}
                      sx={{ marginTop: '1rem', marginBottom: '0' }}
                      inputProps={{maxLength:20}}
                    />
                    {errors?.usuAlteracao?.type && <InputError type={errors.usuAlteracao.type} field='usuAlteracao' />}
                  </Grid>
                  <Grid item xs={6} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }} >
                      <LocalizationProvider dateAdapter={AdapterDateFns} locale={localeSel}>
                        <DateTimePicker
                          disabled={true}
                          label={`${t('pages.chaveValor.dhalt')} *`}
                          value={dhAlteracao}
                          inputFormat={localeInputFormat}
                          mask={localeMask}
                          views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                          onChange={(newValue, keyBoardValue) => { (keyBoardValue?.length === lenDate || newValue === null) && handleChangeDhAlteracao(stringToLocalISO(newValue)); }}
                          onAccept={(newValue) => handleChangeDhAlteracao((newValue === null) ? '' : dateToLocalISO(newValue))}
                          renderInput={(params) =>
                            <TextField
                              id='dhAlteracao'
                              {...params} />}
                        />
                      </LocalizationProvider>
                    </FormControl>
                    {errors?.dhAlteracao?.type && <InputError type={errors.dhAlteracao.type} field='dhAlteracao' />}
                  </Grid>
                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        <ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </Box>
      {(isLoading || isSubmitting) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}
    </LayoutBaseDePagina >
  );
};
